// src/pages/MyBookingsPage.jsx

import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Alert,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Paper,
  Button,
  TextField,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Tooltip,
  Menu,
  ListItemIcon,
  ListItemText
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import moment from "moment-timezone";
import { isFutureBookingInOrgTimezone, formatInOrgTimezone } from "../utils/timezoneUtils";
import { useSnackbar } from "notistack";
import { getMyBookings, cancelMyBooking, updateBooking } from "../services/bookingService";
import { useTimezone } from "../contexts/TimezoneContext";
import EditBookingModal from "../components/EditBookingModal";
import { 
  addToGoogleCalendar, 
  addToOutlookCalendar, 
  addToYahooCalendar, 
  addToAppleCalendar, 
  downloadICSFile 
} from "../utils/calendarUtils";
import { CalendarToday, FileDownload, MoreVert } from "@mui/icons-material";

export default function MyBookingsPage() {
  // short English comments only

  const [bookings, setBookings] = useState([]);
  const [error, setError] = useState("");
  const { orgTimeZone } = useTimezone();

  // filters
  const [selectedRoom, setSelectedRoom] = useState("all");
  const [selectedDate, setSelectedDate] = useState(null);

  // tab state: "future" or "past"
  const [tabValue, setTabValue] = useState("future");

  // dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState(null);

  // edit modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  // calendar menu
  const [calendarMenuAnchor, setCalendarMenuAnchor] = useState(null);
  const [menuBooking, setMenuBooking] = useState(null);

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    fetchBookings();
    // eslint-disable-next-line
  }, []);

  async function fetchBookings() {
    try {
      setError("");
      const data = await getMyBookings();
      setBookings(data);
    } catch (err) {
      setError("Failed to load your bookings");
      enqueueSnackbar("Failed to load bookings", { variant: "error" });
    }
  }

  function handleOpenDialog(bookingId) {
    setBookingToCancel(bookingId);
    setDialogOpen(true);
  }

  function handleCloseDialog() {
    setBookingToCancel(null);
    setDialogOpen(false);
  }

  async function handleConfirmCancel() {
    if (!bookingToCancel) return;
    try {
      await cancelMyBooking(bookingToCancel);
      setBookings((prev) => prev.filter((b) => b._id !== bookingToCancel));
      enqueueSnackbar("Booking canceled successfully", { variant: "success" });
    } catch (err) {
      enqueueSnackbar("Failed to cancel booking", { variant: "error" });
    } finally {
      setDialogOpen(false);
      setBookingToCancel(null);
    }
  }

  // edit booking functions
  function handleOpenEdit(booking) {
    setSelectedBooking(booking);
    setEditModalOpen(true);
  }

  function handleCloseEdit() {
    setSelectedBooking(null);
    setEditModalOpen(false);
  }

  async function handleSaveEdit(bookingId, updateData) {
    try {
      const updatedBooking = await updateBooking(bookingId, updateData);
      // Update the booking in the state
      setBookings((prev) => 
        prev.map((b) => 
          b._id === bookingId ? { ...b, ...updatedBooking } : b
        )
      );
      enqueueSnackbar("Booking updated successfully", { variant: "success" });
    } catch (error) {
      console.error("Error updating booking:", error);
      throw error; // Re-throw to let the modal handle the error display
    }
  }

  // calendar integration functions
  function handleOpenCalendarMenu(event, booking) {
    setCalendarMenuAnchor(event.currentTarget);
    setMenuBooking(booking);
  }

  function handleCloseCalendarMenu() {
    setCalendarMenuAnchor(null);
    setMenuBooking(null);
  }

  function handleAddToGoogleCalendar() {
    if (menuBooking) {
      try {
        addToGoogleCalendar(menuBooking, orgTimeZone);
        enqueueSnackbar("Opening Google Calendar...", { variant: "info" });
      } catch (error) {
        enqueueSnackbar("Failed to open Google Calendar", { variant: "error" });
      }
      handleCloseCalendarMenu();
    }
  }

  function handleAddToOutlookCalendar() {
    if (menuBooking) {
      try {
        addToOutlookCalendar(menuBooking, orgTimeZone);
        enqueueSnackbar("Opening Outlook Calendar...", { variant: "info" });
      } catch (error) {
        enqueueSnackbar("Failed to open Outlook Calendar", { variant: "error" });
      }
      handleCloseCalendarMenu();
    }
  }

  function handleAddToYahooCalendar() {
    if (menuBooking) {
      try {
        addToYahooCalendar(menuBooking, orgTimeZone);
        enqueueSnackbar("Opening Yahoo Calendar...", { variant: "info" });
      } catch (error) {
        enqueueSnackbar("Failed to open Yahoo Calendar", { variant: "error" });
      }
      handleCloseCalendarMenu();
    }
  }

  function handleAddToAppleCalendar() {
    if (menuBooking) {
      try {
        addToAppleCalendar(menuBooking, orgTimeZone);
        enqueueSnackbar("Opening Apple Calendar...", { variant: "info" });
      } catch (error) {
        enqueueSnackbar("Failed to open Apple Calendar", { variant: "error" });
      }
      handleCloseCalendarMenu();
    }
  }

  function handleDownloadICS() {
    if (menuBooking) {
      try {
        downloadICSFile(menuBooking, orgTimeZone);
        enqueueSnackbar("Calendar file downloaded", { variant: "success" });
      } catch (error) {
        enqueueSnackbar("Failed to download calendar file", { variant: "error" });
      }
      handleCloseCalendarMenu();
    }
  }

  // checks if the booking endTime is in the future (in organization timezone)
  function isFutureBooking(b) {
    return isFutureBookingInOrgTimezone(b.endTime, orgTimeZone);
  }

  // separate future vs past
  const future = bookings.filter(isFutureBooking);
  const past = bookings.filter((b) => !isFutureBooking(b));

  // filters for the selected list
  function filterBookings(list) {
    let result = [...list];
    // room filter
    if (selectedRoom !== "all") {
      result = result.filter((b) => b.room && b.room._id === selectedRoom);
    }
    // date filter (using organization timezone)
    if (selectedDate) {
      const daySelected = moment.tz(selectedDate.toDate(), orgTimeZone).startOf("day");
      result = result.filter((b) => {
        const start = moment.tz(b.startTime, orgTimeZone).startOf("day");
        return start.isSame(daySelected, "day");
      });
    }
    return result;
  }

  // gather unique rooms
  const uniqueRooms = [];
  bookings.forEach((b) => {
    if (b.room && !uniqueRooms.find((r) => r._id === b.room._id)) {
      uniqueRooms.push(b.room);
    }
  });

  // pick the relevant data list by tab
  const displayList = tabValue === "future" ? future : past;
  const filtered = filterBookings(displayList);

  function handleTabChange(e, newValue) {
    setTabValue(newValue);
  }

  return (
    <Container sx={{ mt: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Typography variant="h4">
          My Bookings
        </Typography>
        <Chip 
          label={`Times shown in ${orgTimeZone}`} 
          size="small" 
          variant="outlined" 
          color="info"
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tabs for future / past */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Future Bookings" value="future" />
          <Tab label="Past Bookings" value="past" />
        </Tabs>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }} elevation={2}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="filter-room-label">Filter by Room</InputLabel>
            <Select
              labelId="filter-room-label"
              label="Filter by Room"
              value={selectedRoom}
              onChange={(e) => setSelectedRoom(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              {uniqueRooms.map((room) => (
                <MenuItem key={room._id} value={room._id}>
                  {room.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Filter by Date"
              value={selectedDate}
              onChange={(newVal) => setSelectedDate(newVal)}
              slots={{
                textField: TextField
              }}
              slotProps={{
                textField: {
                  sx: { minWidth: 220 }
                }
              }}
            />
          </LocalizationProvider>

          <Button
            variant="outlined"
            onClick={() => {
              setSelectedRoom("all");
              setSelectedDate(null);
            }}
          >
            Clear Filters
          </Button>
        </Box>
      </Paper>

      {filtered.length === 0 ? (
        <Typography>No bookings found.</Typography>
      ) : (
        <Grid container spacing={2}>
          {filtered.map((b) => {
            // checks if it's future or ended, to show or hide "Cancel"
            const futureBooking = isFutureBooking(b);
            return (
              <Grid item xs={12} sm={6} md={4} key={b._id}>
                <Card elevation={2}>
                  <CardContent>
                    <Typography variant="h6">
                      {b.room?.name || "Unknown Room"}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      Start: {formatInOrgTimezone(b.startTime, orgTimeZone)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      End: {formatInOrgTimezone(b.endTime, orgTimeZone)}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ justifyContent: "space-between", alignItems: "center" }}>
                    <Tooltip title="Add to Calendar">
                      <IconButton
                        onClick={(e) => handleOpenCalendarMenu(e, b)}
                        size="small"
                        color="primary"
                      >
                        <CalendarToday />
                      </IconButton>
                    </Tooltip>
                    
                    <Box sx={{ display: "flex", gap: 1 }}>
                      {futureBooking && tabValue === "future" ? (
                        <>
                          <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => handleOpenEdit(b)}
                            size="small"
                          >
                            Edit
                          </Button>
                          <Button
                            variant="contained"
                            color="error"
                            onClick={() => handleOpenDialog(b._id)}
                            size="small"
                          >
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <Typography color="text.secondary" sx={{ ml: 2 }}>
                          Ended
                        </Typography>
                      )}
                    </Box>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>Cancel Booking</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel this booking?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="inherit">
            No
          </Button>
          <Button onClick={handleConfirmCancel} color="error" variant="contained">
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Booking Modal */}
      <EditBookingModal
        open={editModalOpen}
        onClose={handleCloseEdit}
        booking={selectedBooking}
        onSave={handleSaveEdit}
        organizationWorkHours={{ start: "08:00", end: "18:00" }} // You can make this dynamic if needed
      />

      {/* Calendar Integration Menu */}
      <Menu
        anchorEl={calendarMenuAnchor}
        open={Boolean(calendarMenuAnchor)}
        onClose={handleCloseCalendarMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem onClick={handleAddToGoogleCalendar}>
          <ListItemIcon>
            <span style={{ fontSize: '16px' }}>📅</span>
          </ListItemIcon>
          <ListItemText>Google Calendar</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleAddToOutlookCalendar}>
          <ListItemIcon>
            <span style={{ fontSize: '16px' }}>📘</span>
          </ListItemIcon>
          <ListItemText>Outlook Calendar</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleAddToYahooCalendar}>
          <ListItemIcon>
            <span style={{ fontSize: '16px' }}>🟣</span>
          </ListItemIcon>
          <ListItemText>Yahoo Calendar</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleAddToAppleCalendar}>
          <ListItemIcon>
            <span style={{ fontSize: '16px' }}>🍎</span>
          </ListItemIcon>
          <ListItemText>Apple Calendar</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDownloadICS}>
          <ListItemIcon>
            <FileDownload />
          </ListItemIcon>
          <ListItemText>Download .ics file (Universal)</ListItemText>
        </MenuItem>
      </Menu>
    </Container>
  );
}